/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Row, Col, Space } from 'antd';

import { Button, ErrorMessage } from '@atoms';
import { TextField } from '@molecules';
import RichTextField from '@molecules/RichTextField';
import Form from '@organisms/Form';
import { isFormValidationError } from '@utils/form';
import { usePublishPost, useUpsertPost, useUnpublishPost } from '@api/hooks';
import { TOOLBAR_ITEM } from '@atoms/RichTextEditor/constants';

import { FORM_FIELD } from './constants';

const PostForm = ({ parentId, parentType, initialValues, onCancel, onSuccess, liveBlogId }) => {
  const [form] = Form.useForm(FORM_FIELD);
  const [submitError, setSubmitError] = React.useState();
  const handleOnSuccess = React.useCallback(
    (...args) => {
      // eslint-disable-next-line no-unused-expressions
      onSuccess?.(...args);
      if (!initialValues) form.resetFields();
    },
    [form, initialValues, onSuccess],
  );
  const [publish, publishQuery] = usePublishPost({ onSuccess: handleOnSuccess, onError: form.setApiErrors });
  const [upsert, upsertQuery] = useUpsertPost({ onSuccess: handleOnSuccess, onError: form.setApiErrors });
  const [unpublish, unpublishQuery] = useUnpublishPost({ onSuccess });

  const isPublished = Boolean(initialValues?.id && initialValues?.published);

  // Should be unique name, otherwise WYSIWYG editor won't work correctly if multiple blog posts are added
  const formName = React.useMemo(() => `blog-post-form-${Math.random()}`, []);

  const handlePublish = React.useCallback(async () => {
    setSubmitError(null);
    upsertQuery.reset();
    unpublishQuery.reset();
    try {
      const values = await form.validateFields();
      await publish({
        id: initialValues?.contentId || initialValues?.id,
        language: initialValues?.language,
        values,
        parentId,
        parentType,
        liveBlogId,
      });
    } catch (e) {
      if (!isFormValidationError(e)) {
        setSubmitError(e);
      }
    }
  }, [form, initialValues, parentId, parentType, publish, unpublishQuery, upsertQuery, liveBlogId]);

  const handleSaveDraft = React.useCallback(async () => {
    setSubmitError(null);
    publishQuery.reset();
    unpublishQuery.reset();
    try {
      const values = await form.validateFields();
      await upsert({
        id: initialValues?.contentId || initialValues?.id,
        language: initialValues?.language,
        values,
        parentId,
        parentType,
        liveBlogId,
      });
    } catch (e) {
      if (!isFormValidationError(e)) {
        setSubmitError(e);
      }
    }
  }, [form, initialValues, parentId, parentType, publishQuery, unpublishQuery, upsert, liveBlogId]);

  console.log(liveBlogId);

  const handleUnpublish = React.useCallback(async () => {
    setSubmitError(null);
    publishQuery.reset();
    upsertQuery.reset();
    try {
      await form.validateFields();
      await unpublish({ id: initialValues?.contentId || initialValues?.id, language: initialValues?.language });
    } catch (e) {
      if (!isFormValidationError(e)) {
        setSubmitError(e);
      }
    }
  }, [form, initialValues, publishQuery, unpublish, upsertQuery]);

  return (
    <>
      <Form form={form} name={formName} initialValues={initialValues} layout="vertical" component="div">
        <TextField {...FORM_FIELD.TITLE} />
        <RichTextField
          {...FORM_FIELD.TEXT}
          include={[TOOLBAR_ITEM.DELIMITER, TOOLBAR_ITEM.TWITTER, TOOLBAR_ITEM.YOUTUBE, TOOLBAR_ITEM.INSTAGRAM]}
        />
        <ErrorMessage error={[upsertQuery.error, publishQuery.error, unpublishQuery.error, submitError]} />
        <Row justify="space-between" gutter="15">
          <Col>
            <Space size={15}>
              <Button
                loading={publishQuery.isLoading}
                disabled={unpublishQuery.isLoading || upsertQuery.isLoading}
                onClick={handlePublish}
                type="customBlue"
              >{`${isPublished ? 'UPDATE' : 'PUBLISH'} POST`}</Button>
              <Button
                loading={upsertQuery.isLoading}
                disabled={unpublishQuery.isLoading || publishQuery.isLoading}
                onClick={handleSaveDraft}
              >
                SAVE DRAFT
              </Button>
            </Space>
          </Col>
          {initialValues && (
            <Col>
              <Space size={15}>
                <Button
                  loading={unpublishQuery.isLoading}
                  disabled={!isPublished || upsertQuery.isLoading || publishQuery.isLoading}
                  onClick={handleUnpublish}
                >
                  UNPUBLISH
                </Button>
                <Button onClick={onCancel}>CANCEL</Button>
              </Space>
            </Col>
          )}
        </Row>
      </Form>
    </>
  );
};

export default PostForm;
